import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { toVWMobile, toVWDesktop, toREM } from "../../helpers/styles";
import { BREAKPOINTS } from "../../helpers/theme";
import { useMailingData } from "../../hooks/useMailingData";
import Checkbox from "../shared/Checkbox/Checkbox";
import { KlaviyoService } from "../../services/klaviyo";
import { pushGtmDataLayer } from "../../helpers/analytics";
import SectionLines from "../SectionLines/SectionLines";
import { uniqueId } from "lodash";

const SignUpModalFormV2 = (props: { titleId?: string }): JSX.Element => {
  const data = useMailingData();
  const [email, setEmail] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [agreement, setAgreement] = useState<boolean>(true);
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const emailErrorId = uniqueId("mailing-evolution-email-error-");

  const handleChange = useCallback(
    (e) => {
      e.preventDefault();
      setEmail(e.target.value);
      if (showEmailError && e.target.value) {
        setShowEmailError(false);
      }
    },
    [setEmail, showEmailError],
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (email) {
        try {
          await KlaviyoService.subscribeMailingList(data?.klaviyoListId, {
            email,
          });
          setSubmitted(true);
          pushGtmDataLayer({
            event: "newsletter_sign_up",
          });
        } catch (error) {
          console.log(error);
        }
      } else {
        setShowEmailError(true);
      }
    },
    [email, data?.klaviyoListId],
  );

  const handleAgreementChange = () => {
    setAgreement(!agreement);
  };

  return (
    <MailingContainer>
      <Title id={props.titleId}>{data.mainTitle}</Title>
      {submitted ? (
        <Description>{data.successMessage}</Description>
      ) : (
        <>
          <Description>{data.description}</Description>
          <Form>
            <div>
              <EmailInput
                name="email"
                placeholder={data.inputPlaceholder}
                value={email}
                onChange={handleChange}
                type="email"
                autoComplete="email"
                required
                aria-required="true"
                aria-invalid={showEmailError}
                aria-describedby={showEmailError ? emailErrorId : undefined}
                aria-label={data.inputPlaceholder}
              />

              {showEmailError && (
                <EmailError id={emailErrorId}>
                  Please enter an email address.
                </EmailError>
              )}
            </div>
            <MailingButton
              onClick={handleSubmit}
              aria-disabled={!email}
              disabled={data.checkbox ? !agreement : false}
            >
              {data.buttonLabel}
            </MailingButton>
          </Form>
          {data?.termsAndConditions && data?.checkbox ? (
            <CheckboxContainer>
              <Checkbox
                name="agreement"
                checked={agreement}
                onChange={handleAgreementChange}
                label={data?.termsAndConditions}
              />
            </CheckboxContainer>
          ) : (
            <Terms
              dangerouslySetInnerHTML={{
                __html: data?.termsAndConditions,
              }}
            />
          )}
        </>
      )}
    </MailingContainer>
  );
};

const MailingContainer = styled.section`
  ${SectionLines("#6AB437")};
  width: 100%;
  background-color: #012604;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: ${toVWMobile(40)}vw ${toVWMobile(35)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: ${toVWDesktop(80)}vw ${toVWDesktop(415)}vw;
  }

  &::before,
  &::after {
    width: 1px;
    background: #6ab437;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    &::before,
    &::after {
      background: rgba(246, 241, 231, 0.65);
    }
  }
`;

const Title = styled.p`
  width: 100%;
  font-size: ${toREM(20)}rem;
  line-height: normal;
  letter-spacing: -0.8px;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, serif;
  font-weight: 600;
  color: #f6f1e7;
  text-align: center;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${toREM(36)}rem;
    line-height: ${toREM(40)}rem;
    letter-spacing: 0.03em;
    font-family: ${(props) => props.theme.fontGtAlpina},
      ${(props) => props.theme.fallBackFontFamily}, serif;
    font-weight: 500;
  }
`;

const Description = styled.p`
  width: 100%;
  padding-top: ${toVWMobile(7)}vw;
  font-size: ${toREM(12)}rem;
  line-height: normal;
  letter-spacing: 0;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 500;
  color: #f6f1e7;
  text-align: center;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding-top: ${toVWDesktop(25)}vw;
    font-size: ${toREM(14)}rem;
    line-height: normal;
    letter-spacing: 0;
    font-weight: 500;
  }
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${toREM(26)}rem;
  padding-top: ${toVWMobile(30)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    flex-direction: row;
    gap: ${toREM(37)}rem;
    padding-top: ${toVWDesktop(40)}vw;
  }
`;

const EmailInput = styled.input`
  width: 100%;
  min-width: 0;
  height: ${toVWMobile(44)}vw;
  border: 1px solid #f6f1e7;
  border-radius: ${toREM(431.177)}rem;
  background: transparent;
  color: #f6f1e7;
  padding: ${toVWMobile(12)}vw ${toVWMobile(16)}vw ${toVWMobile(12)}vw
    ${toVWMobile(20)}vw;
  font-family: ${(props) => props.theme.fontGtAlpina},
    ${(props) => props.theme.fallBackFontFamily}, serif;
  font-size: ${toREM(12)}rem;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  letter-spacing: -0.6px;

  &::placeholder {
    color: #f6f1e7;
    opacity: 0.5;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #fffbf4;
    outline-offset: 2px;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    box-sizing: content-box;
    width: ${toREM(347)}rem;
    height: ${toREM(28)}rem;
    padding: ${toREM(12)}rem ${toREM(16)}rem ${toREM(12)}rem ${toREM(24)}rem;
    font-size: ${toREM(16)}rem;
    line-height: ${toREM(20)}rem;
    letter-spacing: -0.8px;

    &::placeholder {
      color: #f6f1e7;
      opacity: 0.5;
      font-family: ${(props) => props.theme.fontGtAlpina},
        ${(props) => props.theme.fallBackFontFamily}, serif;
      font-size: ${toREM(16)}rem;
      font-style: normal;
      font-weight: 300;
      line-height: normal;
      letter-spacing: -0.8px;
    }
  }
`;

const EmailError = styled.p`
  margin: 0;
  margin-top: ${toVWMobile(6)}vw;
  color: #fffbf4;
  font-size: ${toREM(12)}rem;
  line-height: ${toREM(16)}rem;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 500;
  padding: 0 ${toVWMobile(20)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-top: ${toREM(8)}rem;
    font-size: ${toREM(12)}rem;
    line-height: ${toREM(16)}rem;
    padding: 0 ${toREM(24)}rem;
  }
`;

const MailingButton = styled.button`
  width: 100%;
  height: ${toVWMobile(44)}vw;
  flex-shrink: 0;
  border: none;
  background: #fffbf4;
  border-radius: ${toREM(431.177)}rem;
  color: #012604;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: ${toVWMobile(12)}vw ${toVWMobile(16)}vw;
  cursor: pointer;
  font-family: ${(props) => props.theme.fontGtAlpina},
    ${(props) => props.theme.fallBackFontFamily}, serif;
  font-size: ${toREM(12)}rem;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  letter-spacing: -0.6px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #fffbf4;
    outline-offset: 2px;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toREM(125)}rem;
    min-width: 0;
    height: ${toREM(54)}rem;
    font-size: ${toREM(16)}rem;
    font-weight: 300;
    letter-spacing: -0.8px;
    line-height: normal;
    padding: ${toREM(16)}rem ${toREM(32)}rem;

    &:hover,
    &:focus-visible,
    &:active {
      background: var(--reskin-color-butterleaf);
      color: var(--reskin-color-dark-green);
    }
  }
`;

const Terms = styled.p`
  width: 100%;
  padding-top: ${toVWMobile(25)}vw;
  font-size: ${toREM(8)}rem;
  line-height: normal;
  letter-spacing: 0;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 500;
  opacity: 0.7;
  color: #f6f1e7;
  text-align: left;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding-top: ${toVWDesktop(20)}vw;
    font-size: ${toREM(10)}rem;
    line-height: normal;
    letter-spacing: 0;
    font-weight: 500;
  }

  a {
    color: #f6f1e7;
    text-decoration: underline;
  }
`;

const CheckboxContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: ${toVWMobile(25)}vw;
  line-height: ${toREM(18)}rem;
  letter-spacing: 0;
  color: #f6f1e7;
  text-align: left;

  > div {
    width: fit-content;
    margin: 0 auto;
    max-width: 100%;
    align-items: center;
    justify-content: center;
    gap: ${toVWMobile(12)}vw;

    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      gap: ${toREM(12)}rem;
    }
  }

  label {
    color: #f6f1e7;
    font-size: ${toREM(8)}rem;
    line-height: normal;
    font-family: ${(props) => props.theme.fontFamily},
      ${(props) => props.theme.fallBackFontFamily}, sans-serif;
    font-weight: 500;
    opacity: 0.7;
    margin-top: ${toVWMobile(2)}vw;
    text-align: left;
    padding-left: 0;

    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      font-size: ${toREM(10)}rem;
      margin-top: ${toVWDesktop(2)}vw;
    }
  }

  label a {
    color: #f6f1e7;
    text-decoration: underline;
  }

  input {
    color: #f6f1e7;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding-top: ${toVWDesktop(20)}vw;
  }
`;

export default SignUpModalFormV2;
